import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../auth/session';

type Prefs = {
  end_user_id: number;
  preferred_channels: string[];
  privacy_sharing: Record<string, boolean>;
  enabled_categories: string[];
  language: string;
  timezone: string;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  preferred_operators: Record<string, number[]>;
  dietary_restrictions: string[];
  updated_at: string;
};

const ALL_CHANNELS = ['push', 'email', 'sms'];
const ALL_CATEGORIES = [
  { key: 'transactional', label: 'Transazionali (conferme, ricevute)', alwaysOn: true },
  { key: 'reminder', label: 'Promemoria (prenotazioni, check-in)' },
  { key: 'marketing', label: 'Marketing (offerte, novità)' },
  { key: 'loyalty', label: 'Loyalty (premi, fiches)' },
];
const ALL_PRIVACY = [
  { key: 'profile', label: 'Profilo (nome, foto)' },
  { key: 'history', label: 'Storico visite' },
  { key: 'loyalty', label: 'Saldo loyalty' },
  { key: 'contact', label: 'Contatti (telefono, email)' },
];
const COMMON_DIETARY = ['Vegetariano', 'Vegano', 'Senza glutine', 'Senza lattosio', 'Halal', 'Kosher'];

export function EndUserPreferencesPage() {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/end-user/preferences');
      if (res.ok) {
        setPrefs(await res.json());
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!prefs) return <div className="sk-form" style={{ color: 'var(--sk-mid)' }}>Caricamento…</div>;

  async function patch(updates: Partial<Prefs>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const res = await apiFetch('/api/v1/end-user/preferences', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        setPrefs(await res.json());
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        setError('Aggiornamento fallito');
      }
    } catch {
      setError('Errore di rete');
    } finally {
      setBusy(false);
    }
  }

  function toggleChannel(ch: string) {
    if (!prefs) return;
    const current = prefs.preferred_channels;
    const next = current.includes(ch) ? current.filter((c) => c !== ch) : [...current, ch];
    void patch({ preferred_channels: next });
  }

  function toggleCategory(key: string, alwaysOn?: boolean) {
    if (!prefs || alwaysOn) return;
    const current = prefs.enabled_categories;
    const next = current.includes(key) ? current.filter((c) => c !== key) : [...current, key];
    void patch({ enabled_categories: next });
  }

  function togglePrivacy(key: string) {
    if (!prefs) return;
    const next = { ...prefs.privacy_sharing, [key]: !prefs.privacy_sharing[key] };
    void patch({ privacy_sharing: next });
  }

  function toggleDietary(item: string) {
    if (!prefs) return;
    const current = prefs.dietary_restrictions;
    const next = current.includes(item) ? current.filter((d) => d !== item) : [...current, item];
    void patch({ dietary_restrictions: next });
  }

  return (
    <div className="sk-form">
      <Link to="/end-user" className="sk-form__back">← Torna al Passaporto</Link>
      <h1 className="sk-form__title">Preferenze</h1>
      <p className="sk-form__lead">
        Gestisci come ricevi notifiche e quali dati condividi con i locali.
      </p>

      {error && <p className="sk-form__error">{error}</p>}
      {saved && <p className="sk-form__saved">✓ Salvato</p>}

      {/* Canali preferiti */}
      <section className="sk-form-section">
        <h2 className="sk-form-section__title">Canali di notifica</h2>
        <p className="sk-form-section__desc">
          Scegli come ricevere le notifiche. Ordine = priorità.
        </p>
        <div className="sk-chip-group">
          {ALL_CHANNELS.map((ch) => (
            <button
              key={ch}
              onClick={() => toggleChannel(ch)}
              disabled={busy}
              className={`sk-toggle-btn ${prefs.preferred_channels.includes(ch) ? 'sk-toggle-btn--active' : ''}`}
            >
              {ch}
            </button>
          ))}
        </div>
      </section>

      {/* Categorie notifiche */}
      <section className="sk-form-section">
        <h2 className="sk-form-section__title">Categorie notifiche</h2>
        <div className="sk-check-list">
          {ALL_CATEGORIES.map((cat) => (
            <label
              key={cat.key}
              className={`sk-check-row ${cat.alwaysOn ? 'sk-check-row--disabled' : ''}`}
            >
              <input
                type="checkbox"
                className="sk-check-row__input"
                checked={prefs.enabled_categories.includes(cat.key) || cat.alwaysOn}
                disabled={cat.alwaysOn || busy}
                onChange={() => toggleCategory(cat.key, cat.alwaysOn)}
              />
              <span className="sk-check-row__label">{cat.label}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Quiet hours */}
      <section className="sk-form-section">
        <h2 className="sk-form-section__title">Orari di quiet</h2>
        <p className="sk-form-section__desc">
          Durante questi orari, le notifiche non transazionali verranno silenziate.
        </p>
        <div className="sk-form-row">
          <label className="sk-form-label">
            Da
            <input
              type="time"
              className="sk-form-input"
              value={prefs.quiet_hours_start || ''}
              disabled={busy}
              onChange={(e) => void patch({ quiet_hours_start: e.target.value || null })}
            />
          </label>
          <label className="sk-form-label">
            A
            <input
              type="time"
              className="sk-form-input"
              value={prefs.quiet_hours_end || ''}
              disabled={busy}
              onChange={(e) => void patch({ quiet_hours_end: e.target.value || null })}
            />
          </label>
        </div>
      </section>

      {/* Privacy */}
      <section className="sk-form-section">
        <h2 className="sk-form-section__title">Privacy e condivisione</h2>
        <p className="sk-form-section__desc">
          Quali dati vuoi condividere con i locali che visiti?
        </p>
        <div className="sk-check-list">
          {ALL_PRIVACY.map((p) => (
            <label key={p.key} className="sk-check-row">
              <input
                type="checkbox"
                className="sk-check-row__input"
                checked={prefs.privacy_sharing[p.key] ?? false}
                disabled={busy}
                onChange={() => togglePrivacy(p.key)}
              />
              <span className="sk-check-row__label">{p.label}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Lingua e fuso orario */}
      <section className="sk-form-section">
        <h2 className="sk-form-section__title">Lingua e fuso orario</h2>
        <div className="sk-form-row">
          <label className="sk-form-label">
            Lingua
            <select
              className="sk-form-input"
              value={prefs.language}
              disabled={busy}
              onChange={(e) => void patch({ language: e.target.value })}
            >
              <option value="it">Italiano</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
            </select>
          </label>
          <label className="sk-form-label">
            Fuso orario
            <select
              className="sk-form-input"
              value={prefs.timezone}
              disabled={busy}
              onChange={(e) => void patch({ timezone: e.target.value })}
            >
              <option value="Europe/Rome">Europe/Rome</option>
              <option value="Europe/London">Europe/London</option>
              <option value="Europe/Paris">Europe/Paris</option>
              <option value="Europe/Berlin">Europe/Berlin</option>
              <option value="America/New_York">America/New_York</option>
            </select>
          </label>
        </div>
      </section>

      {/* Restrizioni alimentari */}
      <section className="sk-form-section">
        <h2 className="sk-form-section__title">Restrizioni alimentari</h2>
        <p className="sk-form-section__desc">
          I ristoranti vedranno le tue restrizioni quando prenoti.
        </p>
        <div className="sk-chip-group">
          {COMMON_DIETARY.map((item) => (
            <button
              key={item}
              onClick={() => toggleDietary(item)}
              disabled={busy}
              className={`sk-toggle-btn ${prefs.dietary_restrictions.includes(item) ? 'sk-toggle-btn--active' : ''}`}
            >
              {item}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export default EndUserPreferencesPage;
